<script setup lang="js">
import { onMounted, ref, computed, watch } from 'vue';
import { useProjectStore } from '@/stores/project.store';
import { useInputFieldStore } from '@/stores/inputField.store';
import { useRouteParams } from '@vueuse/router';
import ModalTemplateSide from '@/components/modals/ModalTemplateSide.vue';
import BasicToggleSwitch from '@/components/toggle-switch.vue';
import useModal from '@/stores/modal.store';
import { storeToRefs } from 'pinia';
import { useValidationRuleStore } from '@/stores/validationRule.store';
import Multiselect from 'vue-multiselect';
import _ from 'lodash';

const inputFieldStore = useInputFieldStore();
const projectStore = useProjectStore();
const validationRuleStore = useValidationRuleStore();

const projectId = useRouteParams('projectId');
const bpId = useRouteParams('bpId');
const nodeId = useRouteParams('nodeId');
const { closeModal } = useModal();
const { project } = storeToRefs(projectStore);
const { typeList, inputField, updateInputFieldRequest } = storeToRefs(inputFieldStore);
const { rules } = storeToRefs(validationRuleStore);

const locale = computed(() => projectStore.project.default_locale);

const title = ref({});
const type = ref('');
const order = ref(0);
const required = ref(false);
const settings = ref(null);
const child_node_ids = ref([]);
const defaultValue = ref(null);
const time = ref(false);
const validationRules = ref([]);

const formFieldType = {
  number: validationRuleStore.NUMBER_FIELD,
  datetime: validationRuleStore.DATE_FIELD,
  text: validationRuleStore.TEXT_FIELD,
  boolean: validationRuleStore.BOOL_FIELD,
  identificator: validationRuleStore.IDENTIFICATOR_FIELD
};

const availableValidationRules = computed(() => {
  if (!type.value) {
    return [];
  }

  return rules.value.filter((r) => r.form_type === formFieldType[type.value])
})

const mask = ref('');
const maskLazyMode = ref(false);
const maskPlaceholder = ref('_');
const maskDefinitions = ref([['', '']]);

const maskTest = computed(() => ({
  mask: mask.value,
  definitions: _.chain(maskDefinitions.value)
    .fromPairs()
    .mapValues((v) => {
      try {
        return new RegExp(v);
      } catch {
        return /./;
      }
    })
    .value(),
  placeholderChar: maskPlaceholder.value,
  lazy: maskLazyMode.value
}));

watch(type, () => {
  validationRules.value = validationRules.value.filter((r) => r.form_type === formFieldType[type.value])
})

onMounted(() => {
  inputFieldStore.clearRequestVariables();
  if (!project.value.locales) {
    projectStore.getProject(projectId.value);
  }
  if (Object.keys(inputField.value).length) {
    title.value = inputField.value.field.title;
    // Тип живёт на привязке, а не на наборе свойств, и неизменяем.
    type.value = inputField.value.type;
    order.value = inputField.value.field.order;
    required.value = inputField.value.field.required;
    if (inputField.value.field.settings?.defaultValue) {
      defaultValue.value = inputField.value.field.settings?.defaultValue;
    }
    if (inputField.value.field.settings?.time) {
      time.value = inputField.value.field.settings?.time;
    }
    if (inputField.value.field.settings?.validationRules) {
      validationRules.value = inputField.value.field.settings?.validationRules;
    }

    if (inputField.value.field.settings?.mask) {
      const maskSettings = inputField.value.field.settings?.mask;
      mask.value = maskSettings.mask;
      maskLazyMode.value = maskSettings.lazy;
      maskPlaceholder.value = maskSettings.placeholder;
      maskDefinitions.value = maskSettings.definitions;
    }

    inputField.value.directional_fields.forEach((d) => {
      child_node_ids.value.push(d.direction.next_bp_node_id);
    });
  }
});

function updateInputField() {
  settings.value = {};
  if (defaultValue.value) {
    settings.value = {};
    settings.value.defaultValue = defaultValue.value;
  }
  if (time.value) {
    settings.value.time = time.value;
  }
  if (validationRules.value.length) {
    settings.value.validationRules = validationRules.value;
  }

  if (type.value == 'text' && mask.value) {
    settings.value.mask = {
      mask: mask.value,
      lazy: maskLazyMode.value,
      placeholder: maskPlaceholder.value,
      definitions: maskDefinitions.value
    };
  }

  inputFieldStore
    .updateInputField(
      projectId.value,
      bpId.value,
      nodeId.value,
      title.value,
      type.value,
      order.value,
      required.value,
      settings.value,
      child_node_ids.value
    )
    .then(() => {
      closeModal();
    });
}

function isErrorField(fieldName) {
  if (updateInputFieldRequest.value.error?.errors) {
    return updateInputFieldRequest.value.error.errors[fieldName] || false;
  }
}
function isNotFieldError() {
  if (!updateInputFieldRequest.value.error?.errors) {
    return updateInputFieldRequest.value.error;
  }
}

function addMaskDefinition() {
  maskDefinitions.value.push(['', '']);
}

function deleteMaskDefinition(definitionIndex) {
  maskDefinitions.value.splice(definitionIndex, 1);
}
</script>

<template>
  <ModalTemplateSide @submit="updateInputField" :disableButtons="updateInputFieldRequest.isLoading">
    <template v-slot:title>Редактировать поле</template>
    <template v-slot:body>
      <div class="mt-3" v-for="locale in project.locales" :key="locale.id">
        <label class="form-label">Наименование поля({{ locale.name }})</label>
        <input type="text" v-model="title[locale.id]" class="form-control" :class="{ error: isErrorField('title') }" />
      </div>
      <p v-if="isErrorField('title')" class="error-mesage">
        <span v-for="error in isErrorField('title')" :key="error">
          {{ error }}
        </span>
      </p>
      <div class="mt-3">
        <label class="form-label">Тип</label>
        <select v-model="type" class="form-select" disabled title="Тип нельзя изменить после создания поля">
          <option v-for="typeKey in Object.keys(typeList)" :key="typeKey" :value="typeKey">
            {{ typeList[typeKey] }}
          </option>
        </select>
        <p v-if="isErrorField('type')" class="error-mesage">
          <span v-for="error in isErrorField('type')" :key="error">
            {{ error }}
          </span>
        </p>
      </div>

      <div class="mt-3" v-if="type">
        <label class="form-label">Кастомные валидации</label>
        <Multiselect v-model="validationRules" :options="availableValidationRules" placeholder="Выбрать..."
          :multiple="true" label="name" track-by="name" class="multiselect-style" />
      </div>

      <template v-if="type === 'text'">
        <div class="mt-2">
          <label class="form-label">Маска</label>
          <input type="text" class="form-control" v-model="mask" />
        </div>
        <div class="mt-2">
          <div class="accordion accordion-flush" id="accordionFlushExample">
            <div class="accordion-item border border-info">
              <h2 class="accordion-header" id="flush-headingOne">
                <button class="accordion-button collapsed" type="button" data-bs-toggle="collapse"
                  data-bs-target="#flush-collapseOne" aria-expanded="false" aria-controls="flush-collapseOne">
                  Дополнительные настройки маски
                </button>
              </h2>
              <div id="flush-collapseOne" class="accordion-collapse collapse" aria-labelledby="flush-headingOne"
                data-bs-parent="#accordionFlushExample">
                <div class="accordion-body">
                  <div class="mb-2">
                    <label class="form-label">Тестирование маски (значение не сохраняется)</label>
                    <input v-mask="maskTest" type="text" class="form-control" />
                  </div>
                  <div class="mb-2 d-flex align-items-center justify-content-between">
                    <label class="form-label">Скрыть символы маски</label>
                    <basic-toggle-switch v-model="maskLazyMode" />
                  </div>
                  <div class="mb-2" v-if="!maskLazyMode">
                    <label class="form-label">Символ заменитель маски</label>
                    <input type="text" v-mask="{ mask: '*', lazy: true }" class="form-control" :value="maskPlaceholder"
                      @complete="maskPlaceholder = $event.detail.value" />
                  </div>
                  <p class="mt-4">Дополнительные символы</p>
                  <div v-for="(def, index) in maskDefinitions" class="mb-2 d-flex justify-content-between"
                    :key="def[0]">
                    <div class="col-2">
                      <input type="text" class="form-control" v-model="def[0]" />
                    </div>
                    <div class="col-8">
                      <input type="text" class="form-control" v-model="def[1]" />
                    </div>
                    <button class="btn btn-primary col-2" :disabled="maskDefinitions.length <= 1"
                      @click="deleteMaskDefinition(index)">
                      Удалить
                    </button>
                  </div>
                  <button class="btn btn-primary w-100" @click="addMaskDefinition">Добавить символ</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>

      <template v-if="type">
        <div class="mt-3">
          <label class="form-label">Значение по умолчанию</label>
          <input type="text" class="form-control" v-model="defaultValue" v-if="type == 'text'" />
          <input type="number" class="form-control" v-model="defaultValue"
            v-if="type == 'number' || type == 'identificator'" />
          <input type="datetime-local" class="form-control" v-model="defaultValue" v-if="type == 'datetime'" />
          <div v-if="type == 'datetime'" class="mt-3 d-flex align-items-center justify-content-between">
            <label class="form-label">Время</label>
            <basic-toggle-switch v-model="time" />
          </div>
          <select v-model="type" class="form-select" v-if="type == 'boolean'">
            <option :value="true">Истина</option>
            <option :value="false">Ложь</option>
          </select>
          <p v-if="isErrorField('defaultValue')" class="error-mesage">
            <span v-for="error in isErrorField('defaultValue')">{{ error }}</span>
          </p>
        </div>
      </template>
      <div class="mt-3">
        <label class="form-label">Дочерние узлы</label>
        <div v-for="directional in inputField.directional_fields" :key="directional.id">
          <b>{{ directional?.direction?.title?.[locale] || '' }}</b>
        </div>
      </div>
      <!-- <div class="mt-3">
        <label class="form-label">Порядок</label>
        <input type="number" v-model="order" class="form-control" min="0"/>
      </div> -->
      <div class="mt-3 d-flex align-items-center justify-content-between">
        <label class="form-label">Обязательность</label>
        <basic-toggle-switch v-model="required" />
      </div>
      <p v-if="isNotFieldError()" class="error-mesage">
        {{ isNotFieldError() }}
      </p>
    </template>
    <template v-slot:submit>Сохранить</template>
  </ModalTemplateSide>
</template>
